import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { GqlProvider } from '@ah/design';
import { onDeepLink, getLaunchDeepLink } from '@ah/native/runtime';
import { AppChromeProvider } from './AppChrome';
import { UpdateGate } from './UpdateGate';
import { useTrackRoute } from '@ah/analytics/shared';
import { gql } from './api';
import { env } from './env';
import { useMe } from './useAuth';
import { Landing } from './routes/Landing';
import { Install } from './routes/Install';
import { Claim } from './routes/Claim';
import { shouldShowInstallGate } from './install';
import { Home } from './routes/Home';
import { Profile } from './routes/Profile';
import { Onboarding } from './routes/Onboarding';
import { Notifications } from './routes/Notifications';
import { Settings } from './routes/Settings';
import {
  Circles,
  SuggestedCircles,
  CircleDetail,
  Coalitions,
  SuggestedCoalitions,
  Leagues,
  SuggestedLeagues,
} from '@ah/circles/mobile';
import { Gatherings, PastGatherings, AttendingGatherings, GatheringDetail, Connections } from '@ah/gatherings/mobile';
import { News, NewsForYou, NewsStory, RecentlyRead, SavedNews } from '@ah/news/mobile';
import {
  Podcasts, PodcastsForYou, PodcastShows, PodcastSeries, PodcastEpisode, RecentlyHeard, SavedPodcasts,
} from '@ah/podcasts/mobile';
import {
  Videos, VideosForYou, VideoChannels, ChannelDetail, RecentlyWatched, SavedVideos, VideoDetail,
} from '@ah/videos/mobile';
import {
  MusicForYou,
  MusicLatest,
  MusicArtists as MusicArtistsView,
  ArtistDetail as MusicArtistDetail,
  RecentlyListened as MusicRecentlyListened,
  SavedMusic,
  SongDetail,
} from '@ah/music/mobile';
import {
  Books,
  BooksForYou,
  BookDetail,
  BookReader,
  RecentlyRead as BooksRecentlyRead,
  SavedBooks,
} from '@ah/books/mobile';
import { InitiativesList, EnrolledInitiatives, InitiativeDetail } from '@ah/initiatives/mobile';
import { MyBucketList, SuggestedBucketList, BucketListItemDetail } from '@ah/bucketlist/mobile';
import { ConversationsList, ChatThread } from '@ah/chat/mobile';
import {
  configurePushClient,
  navigateToUrl,
  onNotificationTapped,
  REGISTER_PUSH_TOKEN,
  UNREGISTER_PUSH_TOKEN,
} from '@ah/notifications/shared';
import { useTimezoneSync } from '@ah/people/shared';

configurePushClient(
  {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
    vapidKey: env.VITE_FIREBASE_VAPID_KEY,
  },
  async (token) => {
    await gql.request<unknown>(REGISTER_PUSH_TOKEN, { token });
  },
  async (token) => {
    await gql.request<unknown>(UNREGISTER_PUSH_TOKEN, { token });
  },
);

// Wrap Routes so Sentry can instrument react-router navigations as transactions.
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function AuthGate({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();

  useEffect(() => {
    if (me) {
      Sentry.setUser({ id: me.id, email: me.email });
    } else if (!isLoading) {
      Sentry.setUser(null);
    }
  }, [me, isLoading]);

  // Once we know who the user is, push their browser timezone up so server
  // renderers (email invites, WhatsApp pings, scheduled push) can format
  // times in their local zone. Debounced to once per tab session.
  useTimezoneSync(!!me);

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!me) {
    // Preserve where the user was trying to go so Landing can send them
    // there after they sign in. Skip the param when they're already at /
    // to keep the bare /login URL clean.
    const path = `${location.pathname}${location.search}${location.hash}`;
    const target = path === '/' ? '/login' : `/login?return_to=${encodeURIComponent(path)}`;
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

function RouteTracker() {
  const loc = useLocation();
  useTrackRoute(loc.pathname, 'mobile');
  return null;
}

// Drain the SW's pending notification-click URL from IndexedDB.
// iOS PWAs cold-launch at the manifest start_url ('/') and ignore the URL we
// pass to clients.openWindow, so the SW writes the target URL to IndexedDB
// (see firebase-messaging-sw.js) and we read it here on first mount.
function drainPendingClickUrl(): Promise<string | null> {
  return new Promise((resolve) => {
    let resolved = false;
    const done = (v: string | null) => { if (!resolved) { resolved = true; resolve(v); } };
    try {
      const req = indexedDB.open('sc-push', 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('pending')) db.createObjectStore('pending');
      };
      req.onerror = () => done(null);
      req.onsuccess = () => {
        const db = req.result;
        const tx = db.transaction('pending', 'readwrite');
        const store = tx.objectStore('pending');
        const getReq = store.get('click');
        getReq.onsuccess = () => {
          const row = getReq.result as { url?: string; ts?: number } | undefined;
          // Ignore stale entries (>30s) so an old click doesn't replay on every
          // launch. Also clear so we never replay even a fresh one twice.
          store.delete('click');
          tx.oncomplete = () => {
            db.close();
            if (row?.url && row.ts && Date.now() - row.ts < 30_000) done(row.url);
            else done(null);
          };
        };
        getReq.onerror = () => done(null);
      };
    } catch { done(null); }
  });
}

// Bridge: route notification-tap URLs into react-router. Three paths feed
// this: (1) IndexedDB drain on first mount (iOS PWA cold launch — the only
// path that works there), (2) the SW postMessage / native tap event via
// onNotificationTapped, and (3) the equivalent on Capacitor for the native
// app shell. The handler is generic — any notification type works as long
// as the server attaches a `url` to the push payload.
function NotificationTapBridge() {
  // See DeepLinkBridge — useNavigate is unstable under BrowserRouter, so
  // we hold it in a ref and run the effect once.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = await drainPendingClickUrl();
      if (cancelled || !url) return;
      navigateToUrl(navigateRef.current, url);
    })();

    const off = onNotificationTapped((msg) => {
      const url = msg.data?.url;
      if (typeof url === 'string' && url) navigateToUrl(navigateRef.current, url);
    });

    return () => {
      cancelled = true;
      off();
    };
    // Empty deps intentional — see comment on navigateRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// Capacitor deep-link bridge. The native shell receives Universal Link
// (iOS) / App Link (Android) URLs via @capacitor/app's `appUrlOpen` event
// and a synchronous cold-start `getLaunchUrl()` accessor. We forward both
// into React Router so a tapped https://mobile.underlings.com/claim/abc
// lands at the equivalent in-app route with no flash.
//
// Cold-start: getLaunchDeepLink() runs once on mount. If it returns a URL
// (because the OS launched the app FROM a link tap), we push it onto the
// history stack on top of the initial '/' entry so the back button still
// returns to home.
//
// Warm: onDeepLink() subscribes for the lifetime of the component.
function DeepLinkBridge() {
  // useNavigate returns a NEW function identity on every render under
  // BrowserRouter (React Router v6). If we used it as an effect dep, the
  // effect would tear down + re-run on every navigation — which resets
  // mountAt and lastHandled, replays getLaunchDeepLink, and re-navigates
  // the user to the launch URL on every back tap. Stable ref instead.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const mountAt = Date.now();
    let cancelled = false;
    // Capacitor surfaces the same cold-start URL twice (sync via
    // App.getLaunchUrl + async via appUrlOpen). Dedup so we don't push
    // two identical history entries.
    let lastHandled: { path: string; at: number } | null = null;

    const handle = (path: string) => {
      const nav = navigateRef.current;
      const now = Date.now();
      if (lastHandled && lastHandled.path === path && now - lastHandled.at < 3000) return;
      const isFirstHandle = lastHandled === null;
      lastHandled = { path, at: now };

      // Cold-start back-button fix: some Capacitor / iOS Universal Link
      // setups open the WebView directly at the deep-link target (or a
      // pathname-equivalent variant without the query), so there's no '/'
      // entry below us and navigate(-1) becomes a no-op — back appears
      // broken. On the first handle shortly after mount, seed '/' first.
      // Warm taps (later than 2s) just push so they don't wipe the user's
      // existing nav stack.
      const isColdStart = isFirstHandle && now - mountAt < 2000;
      if (isColdStart && window.location.pathname !== '/') {
        nav('/', { replace: true });
      }
      nav(path);
    };

    void (async () => {
      const launch = await getLaunchDeepLink();
      if (cancelled || !launch) return;
      handle(launch);
    })();
    const off = onDeepLink((path) => {
      handle(path);
    });
    return () => {
      cancelled = true;
      off();
    };
    // Empty deps intentional — see comment on navigateRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// Catch-all for unmatched routes. Briefly tells the user the link was bad
// before sending them to Home, so a mistyped/stale URL doesn't look like a
// blank-screen crash. Two-second delay is the smallest pause that reads as
// intentional copy rather than a flash.
function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const t = setTimeout(() => {
      navigate('/', { replace: true });
    }, 2000);
    return () => clearTimeout(t);
  }, [navigate]);
  return (
    <div className="min-h-dvh grid place-items-center px-6 text-center">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Invalid link</h1>
        <p className="text-sm text-muted-foreground">
          We couldn’t find a page at <code className="font-mono">{location.pathname}</code>.
        </p>
        <p className="text-sm text-muted-foreground">Taking you home…</p>
      </div>
    </div>
  );
}

function LoginRoute() {
  const [continueInBrowser, setContinueInBrowser] = useState(false);
  if (!continueInBrowser && shouldShowInstallGate()) {
    return <Install onContinue={() => setContinueInBrowser(true)} />;
  }
  return <Landing />;
}

export function App() {
  return (
    <GqlProvider client={gql}>
      <RouteTracker />
      <NotificationTapBridge />
      <DeepLinkBridge />
      <UpdateGate />
      <AppChromeProvider>
      <SentryRoutes>
        <Route path="/login" element={<LoginRoute />} />
        {/*
          Magic-link / pre-auth claim redemption. Tapped from email,
          WhatsApp, or in-app share; the native OS captures it via
          Universal Link / App Link first (DeepLinkBridge routes it
          here), the PWA handles it as a fallback for users without
          the app installed.
        */}
        <Route path="/claim/:code" element={<Claim />} />
        <Route path="/" element={<AuthGate><Home /></AuthGate>} />

        <Route path="/circles"           element={<AuthGate><Circles /></AuthGate>} />
        <Route path="/circles/suggested" element={<AuthGate><SuggestedCircles /></AuthGate>} />
        <Route path="/circles/:id"       element={<AuthGate><CircleDetail /></AuthGate>} />

        <Route path="/coalitions"           element={<AuthGate><Coalitions /></AuthGate>} />
        <Route path="/coalitions/suggested" element={<AuthGate><SuggestedCoalitions /></AuthGate>} />
        <Route path="/games"                element={<AuthGate><Leagues /></AuthGate>} />
        <Route path="/games/suggested"      element={<AuthGate><SuggestedLeagues /></AuthGate>} />

        <Route path="/gatherings"           element={<AuthGate><Gatherings /></AuthGate>} />
        <Route path="/gatherings/attending" element={<AuthGate><AttendingGatherings /></AuthGate>} />
        <Route path="/gatherings/past"      element={<AuthGate><PastGatherings /></AuthGate>} />
        <Route path="/gatherings/:id"       element={<AuthGate><GatheringDetail /></AuthGate>} />

        <Route path="/connections" element={<AuthGate><Connections /></AuthGate>} />

        <Route path="/news"         element={<AuthGate><NewsForYou /></AuthGate>} />
        <Route path="/news/latest"  element={<AuthGate><News /></AuthGate>} />
        <Route path="/news/seen"    element={<AuthGate><RecentlyRead /></AuthGate>} />
        <Route path="/news/saved"   element={<AuthGate><SavedNews /></AuthGate>} />
        <Route path="/news/:id"     element={<AuthGate><NewsStory /></AuthGate>} />

        <Route path="/podcasts"            element={<AuthGate><PodcastsForYou /></AuthGate>} />
        <Route path="/podcasts/latest"     element={<AuthGate><Podcasts /></AuthGate>} />
        <Route path="/podcasts/shows"      element={<AuthGate><PodcastShows /></AuthGate>} />
        <Route path="/podcasts/seen"       element={<AuthGate><RecentlyHeard /></AuthGate>} />
        <Route path="/podcasts/saved"      element={<AuthGate><SavedPodcasts /></AuthGate>} />
        <Route path="/podcasts/series/:id" element={<AuthGate><PodcastSeries /></AuthGate>} />
        <Route path="/podcasts/:id"        element={<AuthGate><PodcastEpisode /></AuthGate>} />

        <Route path="/videos"             element={<AuthGate><VideosForYou /></AuthGate>} />
        <Route path="/videos/latest"      element={<AuthGate><Videos /></AuthGate>} />
        <Route path="/videos/channels"    element={<AuthGate><VideoChannels /></AuthGate>} />
        <Route path="/videos/seen"        element={<AuthGate><RecentlyWatched /></AuthGate>} />
        <Route path="/videos/saved"       element={<AuthGate><SavedVideos /></AuthGate>} />
        <Route path="/videos/channel/:id" element={<AuthGate><ChannelDetail /></AuthGate>} />
        <Route path="/videos/:id"         element={<AuthGate><VideoDetail /></AuthGate>} />

        <Route path="/music"                  element={<AuthGate><MusicForYou /></AuthGate>} />
        <Route path="/music/latest"           element={<AuthGate><MusicLatest /></AuthGate>} />
        <Route path="/music/artists"          element={<AuthGate><MusicArtistsView /></AuthGate>} />
        <Route path="/music/artists/:slug"    element={<AuthGate><MusicArtistDetail /></AuthGate>} />
        <Route path="/music/seen"             element={<AuthGate><MusicRecentlyListened /></AuthGate>} />
        <Route path="/music/saved"            element={<AuthGate><SavedMusic /></AuthGate>} />
        <Route path="/music/:id"              element={<AuthGate><SongDetail /></AuthGate>} />

        <Route path="/library/books"                     element={<AuthGate><BooksForYou /></AuthGate>} />
        <Route path="/library/books/all"                 element={<AuthGate><Books /></AuthGate>} />
        <Route path="/library/books/recent"              element={<AuthGate><BooksRecentlyRead /></AuthGate>} />
        <Route path="/library/books/saved"               element={<AuthGate><SavedBooks /></AuthGate>} />
        <Route path="/library/books/:olid"               element={<AuthGate><BookDetail /></AuthGate>} />
        <Route path="/library/books/:olid/read/:chapter" element={<AuthGate><BookReader /></AuthGate>} />

        <Route path="/initiatives"          element={<AuthGate><InitiativesList /></AuthGate>} />
        <Route path="/initiatives/enrolled" element={<AuthGate><EnrolledInitiatives /></AuthGate>} />
        <Route path="/initiatives/:slug"    element={<AuthGate><InitiativeDetail /></AuthGate>} />

        <Route path="/chat"                  element={<AuthGate><ConversationsList /></AuthGate>} />
        <Route path="/chat/:conversationId"  element={<AuthGate><ChatThread /></AuthGate>} />

        <Route path="/my-life/bucket-list"            element={<AuthGate><MyBucketList /></AuthGate>} />
        <Route path="/my-life/bucket-list/suggested"  element={<AuthGate><SuggestedBucketList /></AuthGate>} />
        <Route path="/my-life/bucket-list/:id"        element={<AuthGate><BucketListItemDetail /></AuthGate>} />

        <Route path="/notifications" element={<AuthGate><Notifications /></AuthGate>} />
        <Route path="/profile" element={<AuthGate><Profile /></AuthGate>} />
        <Route path="/settings" element={<AuthGate><Settings /></AuthGate>} />
        <Route path="/onboarding" element={<AuthGate><Onboarding /></AuthGate>} />

        <Route path="*" element={<NotFound />} />
      </SentryRoutes>
      </AppChromeProvider>
    </GqlProvider>
  );
}
